import type { ReactNode } from 'react';
import { cn } from './ui/utils';

export type Tone = 'slate' | 'amber' | 'rose' | 'emerald' | 'cyan' | 'violet';

// Non-clinical status/category tones only — never the --band-* severity ramp
// (SPEC rule 4: severity color appears on organ scores and nothing else).
const TONE_CLASSES: Record<Tone, string> = {
  slate: 'border-border bg-surface-sunken/40 text-text-tertiary',
  amber: 'border-(--status-attention)/20 bg-status-attention-bg text-status-attention-text',
  rose: 'border-(--status-negative)/20 bg-status-negative-bg text-status-negative-text',
  emerald: 'border-(--status-positive)/20 bg-status-positive-bg text-status-positive-text',
  // Unused today (no `tone="cyan"|"violet"` call sites) — kept for API
  // completeness, referencing the categorical chart primitives directly
  // since they're the only "distinct, non-severity, non-status" hues defined.
  cyan: 'border-(--chart-categorical-2)/20 bg-(--chart-categorical-2)/[0.07] text-(--chart-categorical-2)',
  violet: 'border-(--chart-categorical-6)/20 bg-(--chart-categorical-6)/[0.07] text-(--chart-categorical-6)',
};

export function getToneClasses(tone?: string): string {
  return TONE_CLASSES[tone as Tone] ?? TONE_CLASSES.slate;
}

interface TonePillProps {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}

/** Shared color-tone badge used across sidebar/drawer content (tools, digital twin, risk twin cards). */
export function TonePill({ tone = 'slate', className, children }: TonePillProps) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[10px] font-medium leading-snug wrap-break-word',
        getToneClasses(tone),
        className,
      )}
    >
      {children}
    </span>
  );
}
